import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FlightManagementTable from '../../../airliner/components/FlightManagementTable/FlightManagementTable';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport, faFileExcel, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { useFlightManagement } from '../../../airliner/hooks';
import { fetchFlights } from '../../../store/slices/flightManagementSlice';
import { useAppDispatch } from '../../../store/hooks';

const FlightManagement = () => {
  const dispatch = useAppDispatch();
  const { airlinerId } = useParams(); // Changed from 'id' to 'airlinerId' to match route
  const navigate = useNavigate();
  const { flights, loading, error, loadFlights, removeFlight, cancelFlightById, restoreDeletedFlight, permanentlyDeleteFlight, pagination } = useFlightManagement();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10); // Changed from const to state
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [visibleColumns, setVisibleColumns] = useState({
    regNo: true,
    flightNo: true,
    depAirport: true,
    etd: true,
    arrAirport: true,
    eta: true,
    belPrice: true,
    blPrice: true,
    seatPrice: true,
    seatTotal: true,
    seatAvailable: true,
    flightTime: true,
    status: true,
    action: true
  });

  useEffect(() => {
    const params = {
      sortBy: sortBy,
      sortOrder: sortOrder,
      page: currentPage,
      limit: recordsPerPage,
      search: searchTerm
    };

    if (airlinerId) {
      // Airliner specific - send airliner_id to API
      dispatch(fetchFlights({ airliner_id: airlinerId, ...params }));
    } else {
      // Admin global
      dispatch(fetchFlights(params)).then((res) => {
      });
    }
  }, [dispatch, airlinerId, sortBy, sortOrder, currentPage, recordsPerPage, searchTerm]);

  // Use server-side pagination data
  const serverTotalRecords = pagination?.totalItems || 0;
  const serverTotalPages = pagination?.totalPages || 1;
  
  // Use server data directly - no client-side filtering needed
  const currentData = flights || [];

  // Use server pagination values
  const totalRecords = serverTotalRecords;
  const totalPages = serverTotalPages;

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const toggleColumnVisibility = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const handleExport = (format) => {
    // Export functionality can be implemented here if needed
    setShowExportDropdown(false);
  };

  const handleRecordsPerPageChange = (e) => {
    const newLimit = Number(e.target.value);
    console.log('📊 Records per page changed to:', newLimit);
    setRecordsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page when changing limit
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder(prev => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(key);
      setSortOrder('ASC');
    }
    setCurrentPage(1); // Reset to first page on sort
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (pageNum) => {
    console.log('📄 Page changed to:', pageNum);
    setCurrentPage(pageNum);
  };

  const handleViewFlight = (flightId) => {
    navigate(`/admin/airliners/${airlinerId}/flights/${flightId}`);
  };

  const handleEditFlight = (flightId) => {
    navigate(`/admin/airliners/${airlinerId}/flights/${flightId}/edit`);
  };

  const handleDeleteFlight = async (flightId) => {
    if (window.confirm('There is a reservation for this flight. Click "OK" to proceed, "Cancel" to exit.')) {
      try {
        await removeFlight(flightId);
        toast.success('Flight deleted successfully!');
        loadFlights({ airliner_id: airlinerId });
      } catch (e) {
        toast.error('Failed to delete flight');
      }
    }
  };

  const handleCancelFlight = async (flightId) => {
    if (window.confirm('Are you sure you want to cancel this flight?')) {
      try {
        await cancelFlightById(flightId);
        toast.success('Flight cancelled successfully!');
        loadFlights({ airliner_id: airlinerId });
      } catch (e) {
        toast.error('Failed to cancel flight');
      }
    }
  };

  const handleRestoreFlight = async (flightId) => {
    if (window.confirm('Are you sure you want to restore this flight?')) {
      try {
        await restoreDeletedFlight(flightId);
        toast.success('Flight restored successfully!');
        loadFlights({ airliner_id: airlinerId });
      } catch (e) {
        toast.error('Failed to restore flight');
      }
    }
  };

  const handlePermanentDeleteFlight = async (flightId) => {
    if (window.confirm('Are you sure you want to permanently delete this flight?')) {
      try {
        await permanentlyDeleteFlight(flightId);
        toast.success('Flight permanently deleted successfully!');
        loadFlights({ airliner_id: airlinerId });
      } catch (e) {
        toast.error('Failed to permanently delete flight');
      }
    }
  };

  return (
    <div className="p-4 w-full h-full flex flex-col">
      {/* Breadcrumb & Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="font-semibold text-gray-800">Flight Management</span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center w-full md:w-auto">
          <div className="relative w-full sm:w-auto">
            <button
              className="text-black h-9 w-full sm:w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
              onClick={() => setShowExportDropdown(!showExportDropdown)}
            >
              <FontAwesomeIcon icon={faFileExport} /> Export
            </button>
            {showExportDropdown && (
              <div className="absolute top-full left-0 bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border border-transparent rounded p-0 shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-10 min-w-[155px] mt-[5px] overflow-hidden">
                <div onClick={() => handleExport('excel')} className="py-3 px-4 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 flex items-center gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]">
                  <FontAwesomeIcon icon={faFileExcel} /> Excel
                </div>
                <div onClick={() => handleExport('pdf')} className="py-3 px-4 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 flex items-center gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]">
                  <FontAwesomeIcon icon={faFilePdf} /> PDF
                </div>
              </div>
            )}
          </div>
          <div className="relative w-full sm:w-auto">
            <button
              className="text-black h-9 w-full sm:w-[156px] py-2.5 px-4 text-[14px] font-['Bahnschrift',sans-serif] leading-[16.8px] bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-transparent rounded shadow-none border inline-block mb-0 font-normal text-center whitespace-nowrap align-middle cursor-pointer select-none transition-colors duration-200 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
              onClick={() => setShowColumnDropdown(!showColumnDropdown)}
            >
              Hide/Show Column ▼
            </button>
            {showColumnDropdown && (
              <div className="absolute top-full right-0 bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border border-transparent rounded py-2.5 px-0 shadow-[0_2px_8px_rgba(0,0,0,0.15)] z-10 min-w-[250px] mt-[5px] max-h-[400px] overflow-y-auto">
                {Object.keys(visibleColumns).map(key => (
                  <label
                    key={key}
                    onClick={() => toggleColumnVisibility(key)}
                    className="flex items-center py-2 px-5 cursor-pointer text-[14px] text-black font-normal transition-colors duration-200 gap-2.5 hover:bg-gradient-to-br hover:from-[#ffd200] hover:to-[#ffb300]"
                  >
                    <span className="inline-flex items-center justify-center">
                      <span className="inline-flex items-center justify-center w-4 h-4 min-w-[16px] border border-[#666] rounded-sm bg-white">
                        {visibleColumns[key] && <span className="text-[#2ecc71] text-[14px] font-bold leading-none">✓</span>}
                      </span>
                    </span>
                    <span className="flex-1 whitespace-nowrap">
                      {key === 'regNo' && 'Reg No.'}
                      {key === 'flightNo' && 'Flight No.'}
                      {key === 'depAirport' && 'Dep. Airport'}
                      {key === 'etd' && 'ETD'}
                      {key === 'arrAirport' && 'Arr. Airport'}
                      {key === 'eta' && 'ETA'}
                      {key === 'belPrice' && 'BEL Price'}
                      {key === 'blPrice' && 'BL Price'}
                      {key === 'seatPrice' && 'Seat Price'}
                      {key === 'seatTotal' && 'Total Seat'}
                      {key === 'seatAvailable' && 'Available Seat'}
                      {key === 'flightTime' && 'Flight Time'}
                      {key === 'status' && 'Status'}
                      {key === 'action' && 'Action'}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => navigate(`/admin/airliners`)}
            className="flex items-center gap-2 px-4 py-1.5 text-sm font-semibold text-black bg-[#ffd200] rounded hover:opacity-90 transition-opacity"
          >
            <i className="fas fa-arrow-left"></i>
            <span>Back</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded shadow-sm border border-gray-100 flex-1 flex flex-col pt-4 overflow-hidden">
        <div className="-mt-3 flex-1 overflow-auto">
          <FlightManagementTable
            flights={currentData}
            loading={loading}
            error={error}
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            recordsPerPage={recordsPerPage}
            onRecordsPerPageChange={handleRecordsPerPageChange}
            currentPage={currentPage}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
            onPageChange={handlePageChange}
            visibleColumns={visibleColumns}
            onViewFlight={handleViewFlight}
            onEditFlight={handleEditFlight}
            onDeleteFlight={handleDeleteFlight}
            onCancelFlight={handleCancelFlight}
            onRestoreFlight={handleRestoreFlight}
            onPermanentDeleteFlight={handlePermanentDeleteFlight}
            sortConfig={{ key: sortBy, direction: sortOrder.toLowerCase() }}
            onSortChange={handleSort}
            totalRecords={totalRecords}
            totalPages={totalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default FlightManagement;